import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColorEnum } from './categories';
import { apiFetch } from './client';

export interface Product {
  id: string;
  categoryId: string;
  name: string;
  sku: string | null;
  subtitle: string | null;
  description: string | null;
  priceCents: number;
  thumbLabel: string;
  thumbColor: ColorEnum;
  sort: number;
  inventoryTracked: boolean;
  inventoryStarting: number | null;
  inventoryRemaining: number | null;
  archivedAt: string | null;
}

export interface CreateProductBody {
  categoryId: string;
  name: string;
  sku?: string | null;
  priceCents: number;
  subtitle?: string | null;
  description?: string | null;
  thumbLabel: string;
  thumbColor: ColorEnum;
  sort?: number;
}

export interface PatchProductBody {
  categoryId?: string;
  name?: string;
  sku?: string | null;
  priceCents?: number;
  subtitle?: string | null;
  description?: string | null;
  thumbLabel?: string;
  thumbColor?: ColorEnum;
}

function reorderArray<T>(
  items: T[] | undefined,
  idsInOrder: string[],
  getId: (t: T) => string,
): T[] | undefined {
  if (!items) return items;
  const byId = new Map(items.map((it) => [getId(it), it]));
  const next: T[] = [];
  for (const id of idsInOrder) {
    const found = byId.get(id);
    if (found) next.push(found);
  }
  for (const it of items) {
    if (!idsInOrder.includes(getId(it))) next.push(it);
  }
  return next;
}

export function useProducts(eventId: string, opts: { includeArchived?: boolean } = {}) {
  const { includeArchived = false } = opts;
  // Archived view uses a distinct cache key so it never collides with the
  // active-only list other pages rely on. Mutations invalidate the shorter
  // ['event', eventId, 'products'] prefix, which (React Query exact:false)
  // also matches this key — so no extra invalidation wiring is needed.
  const queryKey = includeArchived
    ? (['event', eventId, 'products', 'withArchived'] as const)
    : (['event', eventId, 'products'] as const);
  return useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<{ products: Product[] }>(
        `/api/v1/admin/events/${eventId}/products${includeArchived ? '?includeArchived=true' : ''}`,
      ).then((r) => r.products),
    enabled: Boolean(eventId),
  });
}

export function useCreateProduct(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProductBody) =>
      apiFetch<{ product: Product }>(`/api/v1/admin/events/${eventId}/products`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.product),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'products'] });
    },
  });
}

export function usePatchProduct(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchProductBody }) =>
      apiFetch<{ product: Product }>(`/api/v1/admin/products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }).then((r) => r.product),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'products'] });
    },
  });
}

export function useArchiveProduct(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/admin/products/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'products'] });
    },
  });
}

export function useRestoreProduct(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ product: Product }>(`/api/v1/admin/products/${id}/restore`, {
        method: 'POST',
      }).then((r) => r.product),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', eventId, 'products'] });
    },
  });
}

export function useReorderProducts(eventId: string) {
  const qc = useQueryClient();
  const key = ['event', eventId, 'products'] as const;
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiFetch<void>(`/api/v1/admin/events/${eventId}/products/reorder`, {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    onMutate: async (ids: string[]) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Product[]>(key);
      qc.setQueryData<Product[] | undefined>(key, (old) =>
        reorderArray(old, ids, (p) => p.id),
      );
      return { previous };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}
